
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { UserProfile, UserRole } from '@/types/database.types';

/**
 * Hook to update a user's role
 */
export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { error } = await supabase
        .from('user_profiles')
        .update({ role } as any) // Type assertion for compatibility
        .eq('id', userId);

      if (error) throw error;
      return { userId, role };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      toast.success('User role updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update user role: ' + error.message);
    },
  });
}

/**
 * Hook to update a user's profile information
 */
export function useUpdateUserProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, updates }: { userId: string; updates: Partial<UserProfile> }) => {
      // Update the user profile in user_profiles table
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates as any) // Type assertion for compatibility 
        .eq('id', userId)
        .select()
        .single();
      
      if (error) throw error;

      // If first_name or last_name was updated, also update the user's display name in auth.users
      if (updates.first_name !== undefined || updates.last_name !== undefined) {
        // Get the current user profile to get both first and last name
        const { data: currentProfile } = await supabase
          .from('user_profiles')
          .select('first_name, last_name')
          .eq('id', userId)
          .single();
        
        if (currentProfile) {
          const displayName = [
            currentProfile.first_name || '',
            currentProfile.last_name || ''
          ].filter(Boolean).join(' ');
          
          // Update the display name in auth.users using the admin API
          const { error: adminError } = await supabase.functions.invoke('invite-user', {
            body: {
              action: 'updateUserDisplayName',
              userId: userId,
              displayName: displayName
            }
          });
          
          if (adminError) {
            console.error('Failed to update display name:', adminError);
            // We don't throw here because the profile was updated successfully
          }
        }
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      toast.success('Profile updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update profile: ' + error.message);
    },
  });
}
